import api, { rawApiClient } from './api';

const trainingApi = {
  // --- Scenarios ---
  listScenarios: (params = {}) => {
    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/training/scenarios${queryString ? `?${queryString}` : ''}`, token);
  },

  getScenario: (id) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/scenarios/${id}`, token);
  },

  // --- Sessions ---
  startSession: (scenarioId, options = {}) => {
    const token = localStorage.getItem('token');
    const body = { scenarioId };
    if (typeof options === 'string') {
      // Backward compat: startSession(scenarioId, assignmentId)
      body.assignmentId = options;
    } else {
      if (options.assignmentId) body.assignmentId = options.assignmentId;
      if (options.cohortId) body.cohortId = options.cohortId;
      if (options.organisationId) body.organisationId = options.organisationId;
      if (options.mode) body.mode = options.mode;
    }
    return api.post('/training/sessions/start', body, token);
  },

  sendMessage: (sessionId, content) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/message`, { content }, token);
  },

  requestAssessment: (sessionId, testKey, administrationMethod = 'conversational') => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/request-assessment`, { testKey, administrationMethod }, token);
  },

  // Text mode only. In a live voice call the proposal goes over the call's
  // socket ('training:voice:propose_goal'), because the call is held on the
  // instance that started it.
  proposeGoal: (sessionId, { description, goalType }) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/propose-goal`, { description, goalType }, token);
  },

  completeSession: (sessionId, reason = 'student_concluded') => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/complete`, { reason }, token);
  },

  abandonSession: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/abandon`, {}, token);
  },

  pauseSession: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/pause`, {}, token);
  },

  resumeSession: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/resume`, {}, token);
  },

  submitFormulation: (sessionId, formulation) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/formulation`, { formulation }, token);
  },

  submitReflection: (sessionId, reflection) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/reflection`, { reflection }, token);
  },

  saveNotes: (sessionId, notes) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/notes`, notes, token);
  },

  getSession: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/sessions/${sessionId}`, token);
  },

  getEvaluation: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/sessions/${sessionId}/evaluation`, token);
  },

  listSessions: (params = {}) => {
    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/training/sessions${queryString ? `?${queryString}` : ''}`, token);
  },

  // --- Assignments ---
  getAssignments: (params = {}) => {
    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/training/assignments${queryString ? `?${queryString}` : ''}`, token);
  },

  // --- Progress ---
  getMyProgress: () => {
    const token = localStorage.getItem('token');
    return api.get('/training/progress/me', token);
  },

  // --- Lecturer Endpoints ---
  lecturerListSessions: (params = {}) => {
    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/training/lecturer/sessions${queryString ? `?${queryString}` : ''}`, token);
  },

  lecturerListStudents: (params = {}) => {
    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/training/lecturer/students${queryString ? `?${queryString}` : ''}`, token);
  },

  lecturerGetSession: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/lecturer/sessions/${sessionId}`, token);
  },

  lecturerGetEvaluation: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/lecturer/sessions/${sessionId}/evaluation`, token);
  },

  lecturerReEvaluate: (sessionId, methodologyId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/lecturer/sessions/${sessionId}/re-evaluate`, { methodologyId }, token);
  },

  // Release the AI evaluation for a therapist-practice session so the therapist
  // can see the score (only relevant when feedbackVisibility === 'supervisor_gated').
  releaseAiEvaluation: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/sessions/${sessionId}/release-ai-evaluation`, {}, token);
  },

  lecturerGetStats: () => {
    const token = localStorage.getItem('token');
    return api.get('/training/lecturer/stats', token);
  },

  // --- Scenario Wizard ---
  startWizardSession: () => {
    const token = localStorage.getItem('token');
    return api.post('/training/scenarios/wizard/start', {}, token);
  },

  sendWizardMessage: (sessionId, message) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/scenarios/wizard/${sessionId}/message`, { message }, token);
  },

  applyWizardConfig: (sessionId, options = {}) => {
    const token = localStorage.getItem('token');
    const body = {};
    if (options.institutionGroupId) body.institutionGroupId = options.institutionGroupId;
    return api.post(`/training/scenarios/wizard/${sessionId}/apply`, body, token);
  },

  // --- Scenario CRUD ---
  // Groups the caller can file new scenarios in, and the default to preselect.
  getAuthorGroups: () => {
    const token = localStorage.getItem('token');
    return api.get('/training/scenarios/author-groups', token);
  },

  // Copy a scenario (with its own persona) into a group: "Use in my group" for
  // lecturers, "Add to Platform Library" for admins. The copy starts hidden from trainees.
  copyScenario: (id, targetGroupId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/scenarios/${id}/copy`, targetGroupId ? { targetGroupId } : {}, token);
  },

  // Draft the "In this scenario" fields from the background brief (not saved).
  draftPatientSituation: (data) => {
    const token = localStorage.getItem('token');
    return api.post('/training/scenarios/draft-patient-situation', data, token);
  },

  createScenario: (data) => {
    const token = localStorage.getItem('token');
    return api.post('/training/scenarios', data, token);
  },

  generatePersonaImage: (scenarioId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/scenarios/${scenarioId}/persona-image`, {}, token);
  },

  deletePersonaImage: (scenarioId) => {
    const token = localStorage.getItem('token');
    return api.delete(`/training/scenarios/${scenarioId}/persona-image`, token);
  },

  generateAvmVoiceGuidance: (scenarioId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/scenarios/${scenarioId}/avm-voice-guidance`, {}, token);
  },

  updateScenario: (id, data) => {
    const token = localStorage.getItem('token');
    return api.put(`/training/scenarios/${id}`, data, token);
  },

  deleteScenario: (id) => {
    const token = localStorage.getItem('token');
    return api.delete(`/training/scenarios/${id}`, token);
  },

  shareScenario: (id, organisationIds) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/scenarios/${id}/share`, { organisationIds }, token);
  },

  unshareScenario: (id, organisationId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/scenarios/${id}/unshare`, { organisationId }, token);
  },

  cloneScenario: (id, targetOrganisationId, includePersona = true) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/scenarios/${id}/clone`, { targetOrganisationId, includePersona }, token);
  },

  // --- Scenario Preview ---
  previewScenario: (id, config) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/scenarios/${id}/preview`, config || {}, token);
  },

  // --- Available Assessments (from TestDefinitions) ---
  getAvailableAssessments: () => {
    const token = localStorage.getItem('token');
    return api.get('/training/scenarios/available-assessments', token);
  },

  // --- Reviewer Config (legacy) ---
  getReviewerConfig: () => {
    const token = localStorage.getItem('token');
    return api.get('/training/reviewer-config', token);
  },

  updateReviewerConfig: (data) => {
    const token = localStorage.getItem('token');
    return api.put('/training/reviewer-config', data, token);
  },

  // --- Evaluation Methodologies ---
  listMethodologies: () => {
    const token = localStorage.getItem('token');
    return api.get('/training/methodologies', token);
  },

  getMethodology: (id) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/methodologies/${id}`, token);
  },

  createMethodology: (data) => {
    const token = localStorage.getItem('token');
    return api.post('/training/methodologies', data, token);
  },

  updateMethodology: (id, data) => {
    const token = localStorage.getItem('token');
    return api.put(`/training/methodologies/${id}`, data, token);
  },

  deleteMethodology: (id) => {
    const token = localStorage.getItem('token');
    return api.delete(`/training/methodologies/${id}`, token);
  },

  exportMethodology: (id) => {
    const token = localStorage.getItem('token');
    return rawApiClient.get(`/training/methodologies/${id}/export`, {
      headers: { 'x-auth-token': token },
      responseType: 'blob',
    }).then(r => r);
  },

  importMethodology: (data) => {
    const token = localStorage.getItem('token');
    return api.post('/training/methodologies/import', data, token);
  },

  // --- Methodology Wizard ---
  startMethodologyWizard: () => {
    const token = localStorage.getItem('token');
    return api.post('/training/methodology-wizard/start', {}, token);
  },

  sendMethodologyWizardMessage: (sessionId, message, attachmentText) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/methodology-wizard/${sessionId}/message`, { message, attachmentText }, token);
  },

  applyMethodologyWizard: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/methodology-wizard/${sessionId}/apply`, {}, token);
  },

  // --- Lecturer Session Export ---
  exportSessionPDF: (sessionId, options = {}) => {
    const token = localStorage.getItem('token');
    return rawApiClient.post(`/training/lecturer/sessions/${sessionId}/export/pdf`, options, {
      headers: { 'x-auth-token': token },
      responseType: 'blob',
    }).then(r => r.data);
  },

  exportSessionMarkdown: (sessionId) => {
    const token = localStorage.getItem('token');
    return rawApiClient.get(`/training/lecturer/sessions/${sessionId}/export/markdown`, {
      headers: { 'x-auth-token': token },
      responseType: 'text',
    }).then(r => r.data);
  },

  exportSessionJSON: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/lecturer/sessions/${sessionId}/export/json`, token);
  },

  exportSessionCSV: (sessionId) => {
    const token = localStorage.getItem('token');
    return rawApiClient.get(`/training/lecturer/sessions/${sessionId}/export/csv`, {
      headers: { 'x-auth-token': token },
      responseType: 'text',
    }).then(r => r.data);
  },

  // --- Lecturer Audio Export ---
  requestAudioExport: (sessionId, config) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/lecturer/sessions/${sessionId}/export-audio`, config, token);
  },

  listAudioExports: () => {
    const token = localStorage.getItem('token');
    return api.get('/training/lecturer/audio-exports', token);
  },

  downloadAudioExport: (jobId) => {
    const token = localStorage.getItem('token');
    return rawApiClient.get(`/training/lecturer/audio-exports/${jobId}/download`, {
      headers: { 'x-auth-token': token },
      responseType: 'blob',
    }).then(r => r);
  },

  // --- Training Voice Config ---
  getVoiceConfig: () => {
    const token = localStorage.getItem('token');
    return api.get('/training/voice-config', token);
  },

  updateVoiceConfig: (data) => {
    const token = localStorage.getItem('token');
    return api.put('/training/voice-config', data, token);
  },

  checkVoiceEligibility: (sessionId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/sessions/${sessionId}/voice-eligibility`, token);
  },

  // --- Organisations ---
  listOrganisations: () => {
    const token = localStorage.getItem('token');
    return api.get('/training/organisations', token);
  },

  getOrganisation: (orgId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/organisations/${orgId}`, token);
  },

  updateOrganisation: (orgId, data) => {
    const token = localStorage.getItem('token');
    return api.put(`/training/organisations/${orgId}`, data, token);
  },

  getOrgMembers: (orgId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/organisations/${orgId}/members`, token);
  },

  addOrgMember: (orgId, data) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/organisations/${orgId}/members`, data, token);
  },

  updateOrgMember: (orgId, userId, data) => {
    const token = localStorage.getItem('token');
    return api.put(`/training/organisations/${orgId}/members/${userId}`, data, token);
  },

  removeOrgMember: (orgId, userId) => {
    const token = localStorage.getItem('token');
    return api.delete(`/training/organisations/${orgId}/members/${userId}`, token);
  },

  getOrgStats: (orgId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/organisations/${orgId}/stats`, token);
  },

  // --- Cohorts ---
  listCohorts: (params = {}) => {
    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/training/cohorts${queryString ? `?${queryString}` : ''}`, token);
  },

  getCohort: (id) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/cohorts/${id}`, token);
  },

  createCohort: (data) => {
    const token = localStorage.getItem('token');
    return api.post('/training/cohorts', data, token);
  },

  updateCohort: (id, data) => {
    const token = localStorage.getItem('token');
    return api.put(`/training/cohorts/${id}`, data, token);
  },

  deleteCohort: (id) => {
    const token = localStorage.getItem('token');
    return api.delete(`/training/cohorts/${id}`, token);
  },

  enrolStudents: (cohortId, data) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/cohorts/${cohortId}/students`, data, token);
  },

  removeStudent: (cohortId, userId) => {
    const token = localStorage.getItem('token');
    return api.delete(`/training/cohorts/${cohortId}/students/${userId}`, token);
  },

  assignLecturer: (cohortId, data) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/cohorts/${cohortId}/lecturers`, data, token);
  },

  removeLecturer: (cohortId, userId) => {
    const token = localStorage.getItem('token');
    return api.delete(`/training/cohorts/${cohortId}/lecturers/${userId}`, token);
  },

  generateInvitation: (cohortId, data = {}) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/cohorts/${cohortId}/invitation`, data, token);
  },

  joinCohort: (invitationCode) => {
    const token = localStorage.getItem('token');
    return api.post('/training/cohorts/join', { invitationCode }, token);
  },

  getCohortStats: (cohortId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/cohorts/${cohortId}/stats`, token);
  },

  getCohortScenarios: (cohortId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/cohorts/${cohortId}/scenarios`, token);
  },

  setCohortScenarios: (cohortId, scenarioAccess) => {
    const token = localStorage.getItem('token');
    return api.put(`/training/cohorts/${cohortId}/scenarios`, { scenarioAccess }, token);
  },

  setCohortScenarioAccess: (cohortId, scenarioId, access) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/cohorts/${cohortId}/scenarios/${scenarioId}`, { access }, token);
  },

  removeCohortScenario: (cohortId, scenarioId) => {
    const token = localStorage.getItem('token');
    return api.delete(`/training/cohorts/${cohortId}/scenarios/${scenarioId}`, token);
  },

  getCohortProgress: (cohortId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/cohorts/${cohortId}/progress`, token);
  },

  // --- Cohort Invitations ---
  createCohortGeneralLink: (cohortId, data) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/cohorts/${cohortId}/invitations/general-link`, data, token);
  },

  createCohortIndividualInvite: (cohortId, data) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/cohorts/${cohortId}/invitations/individual`, data, token);
  },

  listCohortInvitations: (cohortId) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/cohorts/${cohortId}/invitations`, token);
  },

  revokeCohortInvitation: (cohortId, invitationId) => {
    const token = localStorage.getItem('token');
    return api.post(`/training/cohorts/${cohortId}/invitations/${invitationId}/revoke`, {}, token);
  },

  updateCohortInvitation: (cohortId, invitationId, data) => {
    const token = localStorage.getItem('token');
    return rawApiClient.patch(`/training/cohorts/${cohortId}/invitations/${invitationId}`, data, {
      headers: { 'x-auth-token': token },
    });
  },

  validateTrainingCode: (code) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/cohorts/validate-code/${code}`, token);
  },

  // --- Assignments ---
  listAssignments: (params = {}) => {
    const token = localStorage.getItem('token');
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/training/assignments${queryString ? `?${queryString}` : ''}`, token);
  },

  getAssignment: (id) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/assignments/${id}`, token);
  },

  createAssignment: (data) => {
    const token = localStorage.getItem('token');
    return api.post('/training/assignments', data, token);
  },

  updateAssignment: (id, data) => {
    const token = localStorage.getItem('token');
    return api.put(`/training/assignments/${id}`, data, token);
  },

  deleteAssignment: (id) => {
    const token = localStorage.getItem('token');
    return api.delete(`/training/assignments/${id}`, token);
  },

  getAssignmentSubmissions: (id) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/assignments/${id}/submissions`, token);
  },

  getAssignmentStats: (id) => {
    const token = localStorage.getItem('token');
    return api.get(`/training/assignments/${id}/stats`, token);
  },
};

export default trainingApi;
